import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Search } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';
import { COUNTRIES, country as countryMeta } from '../../lib/catalogs';
import { cn } from '../../lib/cn';

/**
 * Polished country dropdown with flag emoji + ISO code + localised
 * `Intl.DisplayNames` country name + type-to-search. Used in onboarding,
 * vendor portal, and anywhere else a country picker appears.
 */
export function CountrySelect({
  value,
  onChange,
  label,
  placeholder,
  className,
  disabled,
}: {
  value: string;
  onChange: (next: string) => void;
  label?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');

  const displayNames = useMemo(() => {
    try {
      return new Intl.DisplayNames([i18n.language], { type: 'region' });
    } catch {
      return null;
    }
  }, [i18n.language]);

  const labelFor = (code: string) => displayNames?.of(code) ?? code;

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return COUNTRIES;
    return COUNTRIES.filter((c) =>
      c.code.toLowerCase().includes(needle) ||
      (labelFor(c.code) ?? '').toLowerCase().includes(needle),
    );
  }, [q, displayNames]);

  const current = countryMeta(value);
  const triggerLabel = current
    ? `${current.emoji}  ${labelFor(current.code)}`
    : (placeholder ?? t('countries.placeholder'));

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <div className="text-xs uppercase tracking-wider text-ink-500 mb-1">{label}</div>
      )}
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              'w-full h-10 px-3 rounded-lg border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900',
              'flex items-center justify-between text-sm gap-2',
              'hover:border-ink-400 focus:outline-none focus:ring-2 focus:ring-accent-500',
              disabled && 'opacity-50 cursor-not-allowed',
            )}
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            <span className="truncate text-left">{triggerLabel}</span>
            <ChevronDown size={14} className="text-ink-400 shrink-0" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            sideOffset={6}
            align="start"
            className="z-50 w-[var(--radix-popover-trigger-width)] min-w-[280px] rounded-xl border border-ink-200 dark:border-ink-800 bg-white dark:bg-ink-900 shadow-lg p-0 data-[state=open]:animate-in data-[state=open]:fade-in-0"
          >
            <div className="px-3 py-2 border-b border-ink-200 dark:border-ink-800 flex items-center gap-2">
              <Search size={14} className="text-ink-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t('countries.search')}
                autoFocus
                className="flex-1 bg-transparent border-0 outline-none text-sm"
              />
            </div>
            <div className="max-h-[320px] overflow-y-auto py-1">
              {filtered.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => { onChange(c.code); setOpen(false); }}
                  className={cn(
                    'w-full text-left px-3 py-1.5 flex items-center gap-2 hover:bg-ink-50 dark:hover:bg-ink-800 text-sm',
                    value === c.code && 'bg-accent-50/40 dark:bg-accent-900/20',
                  )}
                >
                  <span className="text-lg leading-none w-6 shrink-0">{c.emoji}</span>
                  <span className="font-mono text-xs w-8">{c.code}</span>
                  <span className="flex-1 truncate text-ink-700 dark:text-ink-200">{labelFor(c.code)}</span>
                  {value === c.code && <Check size={14} className="text-accent-600" />}
                </button>
              ))}
              {filtered.length === 0 && (
                <div className="px-3 py-6 text-center text-xs text-ink-500">{t('countries.empty')}</div>
              )}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}
